import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "../context/AuthContext";

/* ===================================================== */
/* TEAM FORM MODAL (PRODUCTION READY)                    */
/* ===================================================== */

export default function TeamFormModal({
  open,
  onClose,
  onSaved,
  initial = null,
}) {
  const { user } = useAuth();

  const isEdit =
    Boolean(initial?._id);

  const isContractor =
    initial?.type === "contractor";

  const isFounder =
    initial?.type === "founders";

  const [name, setName] =
    useState("");

  const [type, setType] =
    useState("internal");

  const [isDefault, setIsDefault] =
    useState(false);

  const [leaderId, setLeaderId] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [err, setErr] =
    useState("");

  const inputRef =
    useRef(null);

  /* ===================================================== */
  /* HELPERS                                               */
  /* ===================================================== */

  const getId = (value) => {
    if (!value) {
      return "";
    }

    if (
      typeof value === "object" &&
      value._id
    ) {
      return String(value._id);
    }

    return String(value);
  };

  const members =
    Array.isArray(initial?.members)
      ? initial.members
      : [];

  /* ===================================================== */
  /* INIT                                                  */
  /* ===================================================== */

  useEffect(() => {
    if (!open) {
      return;
    }

    setName(
      initial?.name || ""
    );

    setType(
      initial?.type || "internal"
    );

    setIsDefault(
      Boolean(initial?.isDefault)
    );

    /*
     * Existing team:
     *
     * API may return:
     * leader: { _id, name, email }
     *
     * or:
     * leader: "USER_ID"
     */
    if (initial?.leader) {
      setLeaderId(
        getId(initial.leader)
      );
    } else {
      setLeaderId("");
    }

    setErr("");

    const timer =
      setTimeout(() => {
        inputRef.current?.focus();
      }, 40);

    return () => {
      clearTimeout(timer);
    };
  }, [open, initial]);

  /* ===================================================== */
  /* CREATE DEFAULT LEADER                                 */
  /* ===================================================== */

  useEffect(() => {
    /*
     * New teams automatically use the current
     * authenticated user as their initial leader.
     *
     * This preserves the existing behavior.
     */
    if (
      open &&
      !isEdit &&
      user?._id
    ) {
      setLeaderId(
        String(user._id)
      );
    }
  }, [
    open,
    isEdit,
    user?._id,
  ]);

  if (!open) {
    return null;
  }

  /* ===================================================== */
  /* SUBMIT                                                */
  /* ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* ----------------------------------------------- */
    /* CONTRACTOR PROTECTION                           */
    /* ----------------------------------------------- */

    if (isContractor) {
      setErr(
        "Contractor teams cannot be modified"
      );

      return;
    }

    /* ----------------------------------------------- */
    /* NAME VALIDATION                                  */
    /* ----------------------------------------------- */

    if (!name.trim()) {
      setErr(
        "Team name is required"
      );

      return;
    }

    /* ----------------------------------------------- */
    /* AUTHENTICATION                                   */
    /* ----------------------------------------------- */

    if (!user?._id) {
      setErr(
        "Unable to resolve current user"
      );

      return;
    }

    /* ----------------------------------------------- */
    /* LEADER VALIDATION                                */
    /* ----------------------------------------------- */

    if (isEdit && !isFounder) {
      if (!leaderId) {
        setErr(
          "Please select a team leader"
        );

        return;
      }

      /*
       * The selected leader MUST already be
       * a member of the team.
       *
       * This is also enforced by the backend,
       * but validating here gives the user
       * immediate feedback.
       */
      const leaderIsMember =
        members.some(
          (member) =>
            getId(member) ===
            String(leaderId)
        );

      if (!leaderIsMember) {
        setErr(
          "Team leader must be a member of the team"
        );

        return;
      }
    }

    setLoading(true);
    setErr("");

    try {
      const payload = {
        ...initial,

        name:
          name.trim(),

        type,

        isDefault,
      };

      /* ----------------------------------------------- */
      /* CREATE                                          */
      /* ----------------------------------------------- */

      if (!isEdit) {
        payload.leader =
          user._id;

        payload.members = [
          user._id,
        ];
      }

      /* ----------------------------------------------- */
      /* UPDATE LEADER                                   */
      /* ----------------------------------------------- */

      if (
        isEdit &&
        !isFounder
      ) {
        payload.leader =
          leaderId;
      }

      await onSaved(payload);

      onClose?.();

    } catch (error) {
      setErr(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save team"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 sm:pt-16 px-4">

      {/* BACKDROP */}
      <div
        className="fixed inset-0 bg-black/40"
        onClick={
          loading
            ? undefined
            : onClose
        }
      />

      {/* MODAL */}
      <div className="relative z-50 w-full max-w-lg bg-white rounded-xl shadow-xl border overflow-hidden">

        {/* ================================================= */}
        {/* HEADER                                            */}
        {/* ================================================= */}

        <div className="flex items-center justify-between px-4 py-3 border-b">

          <h3 className="text-lg font-semibold">
            {isEdit
              ? "Edit Team"
              : "New Team"}
          </h3>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Close
          </button>

        </div>

        {/* ================================================= */}
        {/* FORM                                              */}
        {/* ================================================= */}

        <form
          onSubmit={handleSubmit}
        >
          <div className="p-4 space-y-4">

            {/* ============================================= */}
            {/* NAME                                            */}
            {/* ============================================= */}

            <div>
              <label className="block text-sm text-gray-600">
                Team name
              </label>

              <input
                ref={inputRef}
                value={name}
                onChange={(e) =>
                  setName(e.target.value)
                }
                disabled={
                  isContractor ||
                  isFounder
                }
                className="mt-1 w-full border rounded px-3 py-2 disabled:bg-gray-100"
                placeholder="e.g. Engineering Team"
                required
              />
            </div>

            {/* ============================================= */}
            {/* TYPE                                            */}
            {/* ============================================= */}

            {!isContractor && (
              <div>
                <label className="block text-sm text-gray-600">
                  Team type
                </label>

                <select
                  value={type}
                  onChange={(e) =>
                    setType(e.target.value)
                  }
                  disabled={
                    isEdit ||
                    isFounder
                  }
                  className="mt-1 w-48 border rounded px-3 py-2 disabled:bg-gray-100"
                >
                  <option value="internal">
                    Internal
                  </option>
                </select>

                <p className="text-xs text-gray-500 mt-1">
                  Internal teams belong to your company.
                </p>
              </div>
            )}

            {/* ============================================= */}
            {/* TEAM LEADER                                    */}
            {/* ============================================= */}

            {isEdit &&
              !isContractor && (
                <div>
                  <label
                    htmlFor="team-leader"
                    className="block text-sm text-gray-600"
                  >
                    Team leader
                  </label>

                  {isFounder ? (
                    <div className="mt-1 rounded border bg-gray-100 px-3 py-2 text-sm text-gray-700">
                      {initial?.leader?.name ||
                        "Current founder leader"}
                    </div>
                  ) : (
                    <>
                      <select
                        id="team-leader"
                        value={leaderId}
                        onChange={(e) =>
                          setLeaderId(
                            e.target.value
                          )
                        }
                        disabled={
                          loading ||
                          members.length === 0
                        }
                        className="mt-1 w-full border rounded px-3 py-2 bg-white disabled:bg-gray-100"
                      >
                        <option value="">
                          Select team leader
                        </option>

                        {members.map(
                          (member) => {
                            const memberId =
                              getId(member);

                            return (
                              <option
                                key={
                                  memberId
                                }
                                value={
                                  memberId
                                }
                              >
                                {member?.name ||
                                  member?.email ||
                                  memberId}
                              </option>
                            );
                          }
                        )}
                      </select>

                      <p className="text-xs text-gray-500 mt-1">
                        The team leader must be an existing member of this team.
                      </p>
                    </>
                  )}
                </div>
              )}

            {/* ============================================= */}
            {/* NEW TEAM LEADER INFO                           */}
            {/* ============================================= */}

            {!isEdit &&
              !isContractor && (
                <div className="rounded-lg border bg-slate-50 p-3">
                  <div className="text-sm font-medium text-gray-700">
                    Team leader
                  </div>

                  <div className="text-sm text-gray-600 mt-1">
                    {user?.name ||
                      user?.email ||
                      "Current user"}
                  </div>

                  <p className="text-xs text-gray-500 mt-1">
                    You will be added as the initial team leader.
                  </p>
                </div>
              )}

            {/* ============================================= */}
            {/* CONTRACTOR INFO                               */}
            {/* ============================================= */}

            {isContractor && (
              <div className="rounded-lg border bg-indigo-50 p-3 text-sm text-indigo-700">
                This is a contractor team created automatically after a contract was accepted.
                You cannot edit or modify this team.
              </div>
            )}

            {/* ============================================= */}
            {/* DEFAULT                                        */}
            {/* ============================================= */}

            {!isContractor &&
              !isFounder && (
                <div className="flex items-center gap-3">

                  <input
                    id="default"
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) =>
                      setIsDefault(
                        e.target.checked
                      )
                    }
                  />

                  <label
                    htmlFor="default"
                    className="text-sm text-gray-600"
                  >
                    Make default team
                  </label>

                </div>
              )}

            {/* ============================================= */}
            {/* ERROR                                          */}
            {/* ============================================= */}

            {err && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">
                {err}
              </div>
            )}

          </div>

          {/* ================================================= */}
          {/* FOOTER                                            */}
          {/* ================================================= */}

          <div className="flex items-center justify-end gap-3 border-t px-4 py-3 bg-white">

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-3 py-2 rounded border disabled:opacity-50"
            >
              Cancel
            </button>

            {!isContractor && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded bg-slate-900 text-white disabled:opacity-60"
              >
                {loading
                  ? "Saving…"
                  : isEdit
                  ? "Update Team"
                  : "Create Team"}
              </button>
            )}

          </div>

        </form>
      </div>
    </div>
  );
}